import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Tab } from '@enuygun/design-system'
import dayjs from 'dayjs'
import SnapCarousel from 'components/SnapCarousel'
import Slider from 'components/Slider'
import { useIsMobile } from 'hooks/useIsMobile'
import * as S from './Campaigns.styled'
import { CampaignCard } from '@enuygun/frontend-components'
import NextImage from 'components/Image'
import { useIsClient } from 'hooks/useIsClient'
import { useAppState } from 'context/AppContext'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'
import { useDeviceType } from 'hooks/useDeviceType'

// Campaigns için özel lazy loading wrapper - SSR uyumlu
const LazyCampaignCard: React.FC<{
  children: React.ReactNode
  index: number
  isMobile: boolean
  dataTestId?: string
}> = ({ children, index, isMobile, dataTestId = 'lazy-campaign-card' }) => {
  // SSR için true ile başlatıyoruz, böylece JavaScript kapalıyken de içerik render edilir
  const [isVisible, setIsVisible] = useState(true)
  const [isClient, setIsClient] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Client-side'da olduğumuzu işaretle
    setIsClient(true)

    // İlk 3 kartı her zaman göster (viewport'ta görünenler)
    if (index < 3) {
      setIsVisible(true)
      return
    }

    // IntersectionObserver desteklenmiyorsa veya JavaScript kapalıysa içeriği göster
    if (globalThis.window === undefined || !('IntersectionObserver' in globalThis.window)) {
      setIsVisible(true)
      return
    }

    const currentElement = cardRef.current
    if (!currentElement) {
      // Eğer element yoksa, içeriği göster (SSR fallback)
      setIsVisible(true)
      return
    }

    // Client-side'da lazy loading için başlangıçta false yap
    setIsVisible(false)

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.unobserve(currentElement)
        }
      },
      {
        rootMargin: isMobile ? '50px' : '200px',
        threshold: isMobile ? 0.1 : 0.01,
      }
    )

    // DOM'un tamamen yüklenmesini bekleyelim
    const timeoutId = setTimeout(() => {
      if (currentElement) {
        observer.observe(currentElement)
      } else {
        // Element bulunamazsa içeriği göster
        setIsVisible(true)
      }
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [index, isMobile])

  // SSR'da veya JavaScript kapalıyken içeriği direkt göster
  // İlk 3 kart her zaman gösterilir
  const shouldShowContent = index < 3 || !isClient || isVisible
  const placeholderHeight = isMobile ? '180px' : '200px'
  const minHeight = shouldShowContent ? 'auto' : placeholderHeight

  return (
    <>
      {/* CSS animasyonu için style */}
      <style jsx>{`
        @keyframes campaignSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .campaign-spinner {
          animation: campaignSpin 1s linear infinite;
        }
      `}</style>

      <div
        ref={cardRef}
        data-testid={`${dataTestId}-${index}`}
        data-visible={shouldShowContent ? 'true' : 'false'}
        style={{
          minHeight,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {shouldShowContent ? (
          children
        ) : (
          // Placeholder görünümü (sadece client-side lazy loading için)
          <div
            style={{
              width: '100%',
              height: isMobile ? '180px' : '200px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e9ecef',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            <div
              className="campaign-spinner"
              style={{
                width: '32px',
                height: '32px',
                border: '2px solid #e9ecef',
                borderTop: '2px solid #007bff',
                borderRadius: '50%',
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}

const Campaigns = ({ res }) => {
  const isMobile = useIsMobile()
  const isClient = useIsClient()
  const { market } = useAppState()
  const { isTablet } = useDeviceType()

  // Hooks must be called before any early returns
  const [activeButton, setActiveButton] = useState(-1 as any)
  const campaignsContainerRef = useRef<HTMLDivElement>(null)

  // Change to use providers array instead of campaigns
  const providers = res?.[0]?.providers || []

  // Campaign click tracking
  const handleCampaignClick = useCallback(
    (campaign: { campaignTitle: string; campaignSlug: string; providerSlug: string; campaignOrder: number; thumbnailImageUrl: string }, index: number) => {
      const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      const trackingData = {
        category: 'enuygun-homepage',
        page: 'enuygun-homepage',
        type: 'click',
        message: 'campaign_card_click',
        environment: environment,
        requestId,
        slug: campaign.campaignSlug,
        provider: campaign.providerSlug,
        thumbnailImageUrl: campaign.thumbnailImageUrl,
        order: index + 1,
        isMobile: !!isMobile,
        market: market,
      }

      sendTrackerLog(trackingData)
    },
    [isMobile, market]
  )

  // See all campaigns click tracking
  const handleSeeAllClick = useCallback(() => {
    const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: 'enuygun-homepage',
      page: 'enuygun-homepage',
      type: 'click',
      message: 'see_all_campaigns_click',
      environment: environment,
      requestId,
      isMobile: !!isMobile,
      market: market,
    })
  }, [isMobile, market])

  // Early return after all hooks
  if (!res[0] || !providers.length) return null

  // No need for providerArray and providerUniqueArray since we already have unique providers

  const handleFilter = (_category, key) => {
    setActiveButton(key)
  }

  // "Tümü" sekmesinde öne çıkarılacak kampanya slug'ları (sıra korunur)
  const PINNED_ALL_TAB_CAMPAIGN_SLUGS = ['ogrencilere-ozel-her-otobus-biletinde-net-10-indirim']

  const getProviderCampaigns = (providerSlug: string) => {
    const provider = providers.find((p) => p.slug === providerSlug)
    return Object.values(provider?.campaigns?.collection || {})
  }

  // Get all campaigns across providers when filter is empty — pin sadece Tümü için
  const getAllCampaigns = () => {
    const allCampaigns = providers.reduce((acc: any[], provider) => {
      return [...acc, ...Object.values(provider.campaigns?.collection || {})]
    }, [])

    const pinned: any[] = []
    const rest: any[] = []

    allCampaigns.forEach((campaign) => {
      if (PINNED_ALL_TAB_CAMPAIGN_SLUGS.includes(campaign.slug)) {
        pinned.push(campaign)
      } else {
        rest.push(campaign)
      }
    })

    pinned.sort((a, b) => PINNED_ALL_TAB_CAMPAIGN_SLUGS.indexOf(a.slug) - PINNED_ALL_TAB_CAMPAIGN_SLUGS.indexOf(b.slug))

    return [...pinned, ...rest]
  }

  const getTabContent = (items: any[], title: string, slug: string, key: number) => {
    const itemIndex = key + 1
    const tabletContainerWidth = globalThis.window === undefined ? 1120 : Math.min(globalThis.window.innerWidth - 64, 1120)

    let campaignsContent
    if (isTablet) {
      campaignsContent = (
        <S.CampaignsCarouselWrapper>
          <SnapCarousel
            options={{
              perPage: 2.25,
              gap: 16,
            }}
            dataTestId={`campaigns-carousel-${itemIndex}`}
            containerWidth={tabletContainerWidth}
            isMobile={true}
          >
            {renderCampaignCards(items, true)}
          </SnapCarousel>
        </S.CampaignsCarouselWrapper>
      )
    } else if (isMobile) {
      campaignsContent = (
        <S.CampaignsCarouselWrapper>
          <SnapCarousel
            options={{
              perPage: 1,
              gap: 16,
            }}
            dataTestId={`campaigns-carousel-${itemIndex}`}
            containerWidth={1120}
            isMobile={true}
          >
            {renderCampaignCards(items, true)}
          </SnapCarousel>
        </S.CampaignsCarouselWrapper>
      )
    } else {
      campaignsContent = (
        <Slider
          key={key}
          options={{
            pagination: false,
            arrows: true,
            gap: 16,
            perPage: 3,
            padding: 0,
            paddingTop: 0,
          }}
          dataTestId={`campaigns-slider-${itemIndex}`}
        >
          {renderCampaignCards(items, false)}
        </Slider>
      )
    }

    return (
      <Tab.Panel
        unmount={!!isClient}
        title={
          <S.TabsButton active={activeButton === key} isAllTab={key === -1} onClick={() => handleFilter(slug, key)} data-testid={`campaigns-tab-button-${itemIndex}`}>
            {title}
          </S.TabsButton>
        }
        panelKey={key}
        key={key}
        data-testid={`campaigns-tab-panel-${itemIndex}`}
      >
        {campaignsContent}
      </Tab.Panel>
    )
  }

  const renderCampaignCards = (data, isMobileView: boolean) =>
    data
      .filter((item) => dayjs(item.endDate).diff(dayjs()) > 0)
      .map(({ title, slug, provider, thumbnailImageUrl, endDate, ...rest }, index) => {
        // Slider içindeki kartlar için lazy loading'i devre dışı bırak (Slider zaten görünür olanları gösteriyor)
        // Mobil carousel için sadece ilk kartı direkt göster, diğerleri lazy loading ile
        const shouldUseLazyLoading = isMobileView && index >= 1

        // Campaign'in gerçek adı: GraphQL'den gelen title field'ı kullanılıyor
        const campaignTitle = title

        const cardContent = (
          <a
            href={`https://www.enuygun.com/kampanyalar/${provider.slug}/${slug}/`}
            title={campaignTitle}
            target="_blank"
            data-testid={`campaign-card-link-${index}`}
            style={isTablet ? { width: '82%', height: '100%', display: 'block', margin: '0 auto', textAlign: 'left' } : undefined}
            onClick={() =>
              handleCampaignClick(
                { campaignTitle: campaignTitle, campaignSlug: slug, providerSlug: provider.slug, campaignOrder: index + 1, thumbnailImageUrl: thumbnailImageUrl },
                index
              )
            }
          >
            <CampaignCard
              title={campaignTitle}
              image={
                <NextImage
                  loading="lazy"
                  alt={''}
                  width={isMobile ? 354 : 362}
                  height={168}
                  src={`https://cdn2.enuygun.com/media/lib/1x400/uploads/image/${thumbnailImageUrl}`}
                  data-testid={`campaign-card-image-${index}`}
                />
              }
            />
          </a>
        )

        if (!shouldUseLazyLoading) {
          // Desktop Slider veya mobil carousel'in ilk kartı için direkt render et
          return (
            <S.CampaignCardWrapper key={`${provider.slug}-${slug}-${index}`} data-testid={`campaign-card-${index}`}>
              {cardContent}
            </S.CampaignCardWrapper>
          )
        }

        // Mobil carousel için lazy loading kullan (ilk kart hariç)
        return (
          <LazyCampaignCard key={`${provider.slug}-${slug}-${index}`} index={index} isMobile={!!isMobile} dataTestId="campaign-card">
            {cardContent}
          </LazyCampaignCard>
        )
      })

  return (
    <S.CampaignsContainer ref={campaignsContainerRef} data-testid="campaigns-container" isAllTabActive={activeButton === -1}>
      <S.CampaignsHeadContainer data-testid="campaigns-head-container">
        <S.CampaignsHeading data-testid="campaigns-heading">Kampanyalar</S.CampaignsHeading>
        {!isMobile && (
          <S.SeeAllCampaign
            href="https://www.enuygun.com/kampanyalar/"
            title="Tüm kampanyaları gör"
            target="_blank"
            data-testid="campaigns-see-all-link"
            onClick={handleSeeAllClick}
          >
            Tümünü gör
          </S.SeeAllCampaign>
        )}
      </S.CampaignsHeadContainer>

      <Tab
        shape="unstyled"
        onClick={(e, key) => {
          setActiveButton(key)
        }}
      >
        {getTabContent(getAllCampaigns(), 'Tümü', '', -1)}
        {providers.map(({ name, slug }, key) => getTabContent(getProviderCampaigns(slug), name, slug, key))}
      </Tab>
      {isMobile && (
        <S.SeeAllCampaign
          href="https://www.enuygun.com/kampanyalar/"
          title="Tüm kampanyaları gör"
          target="_blank"
          data-testid="campaigns-see-all-link"
          isTablet={isTablet}
          onClick={handleSeeAllClick}
        >
          Tüm kampanyaları gör
        </S.SeeAllCampaign>
      )}
    </S.CampaignsContainer>
  )
}

export default Campaigns
